import { store } from "components/store/store.js";
import { AmplitudeEvent, AmplitudeUserProp } from "components/shared/amplitude.js";
import { i18n } from "../../entrypoints/plugins/i18n.js";
import { messages } from "../../entrypoints/plugins/notifications-i18n.js";
const $i18n = i18n(messages).global;
import { createIntercomEvent } from '../shared/intercom.js'
import { http } from '../../utils/http.js'
import { SearchAnalytics } from '../shared/searchAnalytics.js'
import { credentialPath } from '../credentials/credentialFields.js'

let currentStreamController = null;

function criteriaSource(params = {}, requestSource = '') {
  if (params.command || requestSource === 'Command') return 'command';
  if (params.offer_id || params.pnr) return 'copy_option';
  if (params.text || params.image || requestSource === 'Copilot') return 'one_shot';
  return 'ui';
}

function responseCriteria(response = {}) {
  const filters = { ...(response.filters || {}) };
  const connection = response.sort?.connection;

  if (connection === 0) filters.direct = true;
  if (connection === 1) filters.up_to_one_connection = true;
  delete filters.cheapest;
  delete filters.fastest;

  return filters;
}

function captureFilterState(state) {
  return {
    searchFilters: { ...(state.searchFilters || {}) },
    searchFilterSources: { ...(state.searchFilterSources || {}) },
    extraFilters: [...(state.extraFilters || [])],
    sortPreference: state.sortPreference ? { ...state.sortPreference } : null,
    serverCarrierScope: state.serverCarrierScope == null ? null : [...state.serverCarrierScope]
  };
}

function validFilterState(filters = {}) {
  // Airports may now describe an origin, destination or connection. Only a
  // required transfer country necessarily contradicts a direct flight.
  const countryTransfer = filters.country_transfer;
  const hasCountryTransfer = Array.isArray(countryTransfer)
    ? countryTransfer.length > 0
    : Boolean(countryTransfer);

  return !(filters.direct === true && hasCountryTransfer) &&
    !(filters.direct === true && filters.up_to_one_connection === true);
}

export default {
  createPartner({ commit, state }, { data, resolve, token }) {
    postRequest({
      commit,
      token,
      params: data,
      url: "/agencies",
      callBack: (res) => {
        commit("pushAgency", { agency: res.partner });
        showNotification(commit, $i18n.t("createPartner"), "success");
        resolve();
      },
    });
  },

  createCompany({ commit, state }, { data, resolve, token }) {
    postRequest({
      commit,
      token,
      params: data,
      url: "/companies",
      callBack: (res) => {
        commit("pushCompany", { company: res.company });
        showNotification(commit, $i18n.t("createCompany"), "success");
        resolve();
      },
    });
  },

  fetchCompany({ commit }, { companyId, resolve, reject }) {
    getRequest({
      commit,
      url: `/companies/${companyId}`,
      callBack: (res) => {
        commit("mergeCompany", { company: res.company });
        if (resolve) {
          resolve(res.company);
        }
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      },
    });
  },

  deleteCompany({ commit, state }, { companyId, resolve, token }) {
    deleteRequest({
      commit,
      token,
      url: `/companies/${companyId}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("deleteCompany"), "success");
        commit("removeCompany", { companyId });
        commit("addRequests", { requests: res });
        resolve();
      },
    });
  },

  deleteUser({ commit, state }, { relationshipId, id, companyId, token }) {
    deleteRequest({
      commit,
      token,
      url: `/agent_company_relationships/${relationshipId}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("deleteUser"), "success");
        commit("removeUser", { id, companyId });
        commit("addRequest", { request: res.join_agency });
      },
    });
  },

  connectToPartner({ commit, state }, { data, token }) {
    postRequest({
      commit,
      token,
      params: data,
      url: "/join_agency_requests",
      callBack: (res) => {
        showNotification(commit, $i18n.t("connectToPartner"), "success");
        commit("changeStatus", { request: res.join_agency_request });
        new AmplitudeEvent("Connected");
      },
    });
  },

  disconnectFromPartner({ commit, state }, { id, token }) {
    patchRequest({
      commit,
      token,
      url: `/join_agency_requests/${id}`,
      callBack: (res) => {
        new AmplitudeEvent("Disconnect");
        showNotification(commit, $i18n.t("disconnectFromPartner"), "success");
        commit("deleteAgency", { id });
        commit("resetStatus", { request: res.join_agency_request });
      },
    });
  },

  responseToConnectionRequest(
    { commit, state },
    { data, requestId, resolve, companyId, token }
  ) {
    patchRequest({
      commit,
      token,
      params: data,
      url: `/join_agency_requests/${requestId}`,
      callBack: (res) => {
        new AmplitudeEvent("Add Agent", { partner: res.join_agency.agency }, res.join_agency.avia_manager.drct_id)
        showNotification(
          commit,
          $i18n.t("responseToConnectionRequest"),
          "success"
        );
        commit("removeRequest", { request: res.join_agency });
        if (companyId) {
          commit("addManagerToCompany", {
            companyId,
            request: res.join_agency,
          });
        }
        if (resolve) {
          resolve();
        }
      },
    });
  },

  removePartner({ commit, state }, { agencyId, resolve, token }) {
    deleteRequest({
      commit,
      token,
      url: `/agencies/${agencyId}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("removePartner"), "success");
        commit("removePartner", { id: res.partner_id });
        resolve();
      },
    });
  },

  editSupervisorInfo(
    { commit, state },
    { data, id, partnerId, resolve, token }
  ) {
    patchRequest({
      commit,
      token,
      params: data,
      url: `/agencies/${partnerId}/supervisors/${id}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("editSupervisorInfo"), "success");
        commit("updateSupervisor", { supervisor: res.supervisor, partnerId });
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  updateAgencyMarkup(
    { commit, state },
    { token, params, url, markups, resolve }
  ) {
    patchRequest({
      commit,
      token,
      params,
      url,
      callBack: (res) => {
        showNotification(commit, $i18n.t("updateMakrup"), "success");
        commit("updateAgencyMarkup", {
          markups: markups || params?.transaction_fees?.markups
        });
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  updateCompanyMarkup(
    { commit, state },
    { token, params, url, markups, id, resolve }
  ) {
    patchRequest({
      commit,
      token,
      params,
      url,
      callBack: (res) => {
        showNotification(commit, $i18n.t("updateMakrup"), "success");
        commit("updateCompanyMarkup", {
          markups: markups || params?.transaction_fees?.markups,
          id
        });
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  removeSupervisor({ commit, state }, { id, partnerId, token, resolve }) {
    deleteRequest({
      commit,
      token,
      url: `/agencies/${partnerId}/supervisors/${id}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("removeSupervisor"), "success");
        commit("removeSupervisor", { supervisorId: id, partnerId: partnerId });
        resolve();
      },
    });
  },

  createSupervisor({ commit, state }, { data, id, resolve, token }) {
    postRequest({
      commit,
      token,
      params: data,
      url: `/agencies/${id}/supervisors`,
      callBack: (res) => {
        commit("createSupervisor", { supervisor: res, partnerId: id });
        new AmplitudeEvent("Create Supervisor", { partner: res.agency }, res.drct_id)
        showNotification(commit, $i18n.t("createSupervisor"), "success");
        resolve(res);
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  createSupport({ commit, state }, { data, resolve, token }) {
    postRequest({
      commit,
      token,
      params: data,
      url: `/supports`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("createSupport"), "success");
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  editMarkupData({ commit }, { data, id, resolve, token }) {
    customRequest({
      method: "put",
      commit,
      token,
      params: data,
      url: `/agency_transaction_fees/${id}`,
      callBack: () => {
        showNotification(commit, $i18n.t("updateTransactionFee"), "success");
        resolve(true);
      },
      errorCallBack: () => {
        resolve(false);
      },
    });
  },

  editPartnerInfo({ commit, state }, { data, id, resolve, token }) {
    patchRequest({
      commit,
      token,
      params: data,
      url: `/agencies/${id}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("editPartnerInfo"), "success");
        commit("updatePartner", { partner: res.partner });
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  updateBalance(
    { commit, state },
    { data, companyId, company, resolve, token }
  ) {
    postRequest({
      commit,
      token,
      params: data,
      url: "/transactions",
      callBack: (res) => {
        showNotification(commit, $i18n.t("updateBalance"), "success");
        commit("updateBalance", {
          balance: res.balance_amount,
          id: companyId,
          company,
        });
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  updateAgencyBalance(
    { commit, state },
    { data, agencyId, resolve, token }
  ) {
    postRequest({
      commit,
      token,
      params: data,
      url: `/agencies/${agencyId}/transactions`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("updateBalance"), "success");
        if (res.agency) {
          commit("updatePartner", { partner: res.agency });
        }
        if (resolve) {
          resolve();
        }
      },
      errorCallBack: () => {
        if (resolve) {
          resolve();
        }
      },
    });
  },

  updateAgencyCreditLimit(
    { commit, state },
    { data, agencyId, resolve, token }
  ) {
    postRequest({
      commit,
      token,
      params: data,
      url: `/agencies/${agencyId}/transactions`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("updateBalance"), "success");
        if (res.agency) {
          commit("updatePartner", { partner: res.agency });
        }
        if (resolve) {
          resolve();
        }
      },
      errorCallBack: () => {
        if (resolve) {
          resolve();
        }
      },
    });
  },

  updateAgencyCreditLimitUnlimited(
    { commit, state },
    { agencyId, unlimited, token, resolve }
  ) {
    patchRequest({
      commit,
      token,
      params: { unlimited_credit_limit: unlimited },
      url: `/agencies/${agencyId}/balance`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("updateBalance"), "success");
        if (res.agency) {
          commit("updatePartner", { partner: res.agency });
        }
        if (resolve) {
          resolve();
        }
      },
      errorCallBack: () => {
        if (resolve) {
          resolve();
        }
      },
    });
  },

  updateCompanyBalanceUnlimited(
    { commit, state },
    { companyId, unlimited, token }
  ) {
    patchRequest({
      commit,
      token,
      params: { unlimited },
      url: `/company_balances/${companyId}`,
      callBack: (res) => {
        if (res.unlimited) {
          showNotification(commit, $i18n.t("changeMainStatus"), "success");
        } 
        commit("updateCompanyBalanceUnlimited", {
          unlimited: res.unlimited,
          id: companyId,
        });
      },
    });
  },

  changeMainStatus({ commit, state }, { token }) {
    patchRequest({
      commit,
      token,
      url: "/ticketing/main_gate",
      callBack: (res) => {
        showNotification(commit, $i18n.t("changeMainStatus"), "success");
        commit("changeMainStatus", { active: res.active });
      },
    });
  },

  changePartnersStatus({ commit, state }, { agencyId, id, token, resolve }) {
    patchRequest({
      commit,
      token,
      url: `/ticketing/agency_gates/${id}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("changePartnersStatus"), "success");
        commit("changePartnersStatus", { active: res.active, agencyId });
        resolve();
      },
    });
  },

  duplicatePartner({ commit, state }, { id, token, resolve }) {
    postRequest({
      commit,
      token,
      url: `/agencies/${id}/duplicate`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("duplicatePartnersSuccess"), "success");
        resolve(res);
      },
      errorCallBack: (res) => {
        resolve(res);
      }
    });
  },

  changeConsolidatorsStatus({ commit, state }, { token }) {
    patchRequest({
      commit,
      token,
      url: "/ticketing/agency_gate",
      callBack: (res) => {
        showNotification(
          commit,
          $i18n.t("changeConsolidatorsStatus"),
          "success"
        );
        commit("changeConsolidatorStatus", { active: res.active });
      },
    });
  },

  changeAgenciesStatus({ commit, state }, { companyId, id, token }) {
    patchRequest({
      commit,
      token,
      url: `/ticketing/company_gates/${id}`,
      callBack: (res) => {
        if (res.active) {
          showNotification(
            commit,
            $i18n.t("changeAgenciesStatusActive"),
            "success"
          );
        } else {
          showNotification(
            commit,
            $i18n.t("changeAgenciesStatusBlocked"),
            "success"
          );
        }
        commit("changeAgenciesStatus", { active: res.active, companyId });
      },
    });
  },

  updateAdminsChannel: patchAndCommit({
    url: "/ticketing/channel_gate",
    params: ({ data }) => data,
    notifyKey: "updateAdminsChannel",
    mutation: "changeAdminsChannel",
    payload: (res, { channel }) => ({ active: res.active, channel }),
  }),

  updateSourceChannel: patchAndCommit({
    url: ({ channel, type }) =>
      `/ticketing/source_channels/${channel}?type=${type}`,
    notifyKey: "updateAdminsChannel",
    mutation: "changeSourceChannel",
    payload: (res, { channel, type }) => ({
      active: res.active,
      channel,
      type,
    }),
  }),

  updateSupervisorsChannel({ commit, state }, { channelId, token, active, formOfPayment = null, formOfPaymentId = null, formOfPaymentProduct = null, resolve, reject}) {
    const payload = {};
    if (active !== undefined) payload.on_by_admin = active;
    if (formOfPayment != null) payload.form_of_payment = formOfPayment;
    if (formOfPaymentId != null) payload.ticketing_form_of_payment_id = formOfPaymentId;
    if (formOfPaymentProduct != null) payload.form_of_payment_product = formOfPaymentProduct;
    const params = { agency_channel: payload };
    patchRequest({
      commit,
      token,
      params,
      url: `/ticketing/agency_channels/${channelId}`,
      callBack: (res) => {
        let message = $i18n.t("updateSupervisorsChannel");
        if(formOfPayment != null || formOfPaymentProduct != null) {
          message = 'Form of payment was updated'
        }
        if (formOfPaymentId != null){
          message = 'Form of payment was updated'
        }
        showNotification(
          commit,
          message,
          "success"
        );
        commit("changeSupervisorsChannel", { active: res.active, stored_form_of_payment: res.stored_form_of_payment, form_of_payment: res.form_of_payment, form_of_payment_product: res.form_of_payment_product, channelId });
        if (resolve) {
          resolve();
        }
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      }
    });
  },

  createChannel({ commit, state }, { id, channel, token }) {
    postRequest({
      commit,
      token,
      params: { channel: { agency_id: id, name: channel } },
      url: "/ticketing/agency_channels",
      callBack: (res) => {
        showNotification(commit, $i18n.t("createChannel"), "success");
        commit("updatePartnerChanel", {
          id,
          channelTitle: channel,
          channelId: res.channel.id,
        });
      },
    });
  },

  deleteChannelFormOfPayment({ commit, state }, { id, channelId, agencyId, resolve, token }) {
    deleteRequest({
      commit,
      token,
      url: `/ticketing/agency_channels/${channelId}/form_of_payments/${id}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("cardRemoved"), "success");
        commit("updateChannelsFormOfPayment", { agencyId, channelId, card: {} });
        resolve();
      },
    });
  },

  updateFormOfPayment({ commit, state }, { cardId, data, resolve, token }) {
    patchRequest({
      commit,
      token,
      params: data,
      url: `/form_of_payments/${cardId}`,
      callBack: (res) => {
        showNotification(commit, "Card updated", "success");
        commit("updateAdminFormOfPayment", { cardId, formOfPayment: res});
        resolve();
      },
    });
  },

  createFormOfPayment({ commit, state }, { data, resolve, token }) {
    postRequest({
      commit,
      token,
      params: data,
      url: `/form_of_payments/`,
      callBack: (res) => {
        showNotification(commit, "Card added", "success");
        commit("addAdminFormOfPayment", { formOfPayment: res });
        resolve();
      },
    });
  },

  deleteFormOfPayment({ commit, state }, { cardId, resolve, token }) {
    deleteRequest({
      commit,
      token,
      url: `/form_of_payments/${cardId}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("cardRemoved"), "success");
        commit("deleteAdminFormOfPayment", { cardId });
        resolve();
      },
    });
  },

  getAdminCredentials({ commit }, { resolve, reject } = {}) {
    getRequest({
      commit,
      url: "/credentials",
      params: { headers: { Accept: "application/json" } },
      silent: true,
      callBack: (res) => {
        // Only a real payload replaces the lists, so an HTML or empty body never wipes them.
        if (Array.isArray(res?.credentials)) {
          commit("credentials", { credentials: res.credentials });
        }
        if (Array.isArray(res?.empty_credentials)) {
          commit("emptyCredentials", { emptyCredentials: res.empty_credentials });
        }
        if (resolve) resolve(res);
      },
      errorCallBack: (error) => rejectWithErrors({ commit, error, reject }),
    });
  },

  createAdminCredential({ commit }, { data, token, resolve, reject }) {
    postRequest({
      commit,
      token,
      params: data,
      url: "/credentials",
      silent: true,
      callBack: (res) => {
        commit("createAdminCredential", { credential: res });
        showNotification(commit, $i18n.t("credentialCreated"), "success");
        if (resolve) resolve(res);
      },
      errorCallBack: (error) => rejectWithStatus({ commit, error, reject }),
    });
  },

  updateAdminCredential({ commit }, { credential, data, token, resolve, reject }) {
    patchRequest({
      commit,
      token,
      params: data,
      url: credentialPath(credential),
      silent: true,
      callBack: (res) => {
        commit("updateAdminCredential", { credential: res });
        showNotification(commit, $i18n.t("credentialUpdated"), "success");
        if (resolve) resolve(res);
      },
      errorCallBack: (error) => rejectWithStatus({ commit, error, reject }),
    });
  },

  deleteAdminCredential({ commit }, { credential, token, resolve, reject }) {
    deleteRequest({
      commit,
      token,
      url: credentialPath(credential),
      silent: true,
      callBack: (res) => {
        commit("deleteAdminCredential", { credential });
        const unlinked = Number(res?.unlinked_agencies) || 0;
        const message = unlinked > 0
          ? $i18n.t("credentialDeletedUnlinked", unlinked)
          : $i18n.t("credentialDeleted");
        showNotification(commit, message, "success");
        if (resolve) resolve(res);
      },
      errorCallBack: (error) => rejectWithErrors({ commit, error, reject }),
    });
  },

  getAgencyChannelCredential({ commit }, { channelId, resolve, reject }) {
    getRequest({
      commit,
      url: `/ticketing/agency_channel_credentials/${channelId}`,
      params: { headers: { Accept: "application/json" } },
      silent: true,
      callBack: (res) => {
        if (resolve) resolve(res);
      },
      errorCallBack: (error) => rejectWithErrors({ commit, error, reject }),
    });
  },

  linkAgencyChannelCredential(
    { commit },
    { channelId, credentialId, carriers, token, resolve, reject }
  ) {
    const params = { carriers };
    if (credentialId != null) params.credential_id = credentialId;

    patchRequest({
      commit,
      token,
      params,
      url: `/ticketing/agency_channel_credentials/${channelId}`,
      silent: true,
      callBack: (res) => {
        commitAgencyChannelCredential(commit, channelId, res);
        const message = credentialId != null ? "credentialLinked" : "credentialCarriersUpdated";
        showNotification(commit, $i18n.t(message), "success");
        if (res?.credential && res.credential.active === false) {
          showNotification(commit, $i18n.t("credentialLinkedInactive"), "warning");
        }
        if (resolve) resolve(res);
      },
      errorCallBack: (error) => rejectWithErrors({ commit, error, reject }),
    });
  },

  unlinkAgencyChannelCredential({ commit }, { channelId, token, resolve, reject }) {
    deleteRequest({
      commit,
      token,
      url: `/ticketing/agency_channel_credentials/${channelId}`,
      silent: true,
      callBack: (res) => {
        commitAgencyChannelCredential(commit, channelId, res);
        showNotification(commit, $i18n.t("credentialUnlinked"), "success");
        if (resolve) resolve(res);
      },
      errorCallBack: (error) => rejectWithErrors({ commit, error, reject }),
    });
  },

  saveFormOfPaymentToChannel({ commit, state }, { data, channelId, agencyId, resolve, token }) {
    postRequest({
      commit,
      token,
      params: data,
      url: `/ticketing/agency_channels/${channelId}/form_of_payments`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("cardCreated"), "success");
        commit("updateChannelsFormOfPayment", { agencyId, channelId, card: res });
        resolve();
      },
    });
  },

  removeChannel({ commit, state }, { agencyId, channelId, resolve, token }) {
    deleteRequest({
      commit,
      token,
      url: `/ticketing/agency_channels/${channelId}`,
      callBack: (res) => {
        showNotification(commit, $i18n.t("removeChannel"), "success");
        commit("removeChanell", { agencyId, channelId });
        resolve();
      },
    });
  },

  createCredentialsData(
    { commit, state },
    { params, url, channelId, resolve, token, reject }
  ) {
    postRequest({
      commit,
      token,
      url,
      params,
      callBack: (res) => {
        if (res.credential.active) {
          showNotification(
            commit,
            $i18n.t("createCredentialsDataActive"),
            "success"
          );
        } else {
          showNotification(
            commit,
            $i18n.t("createCredentialsDataInactive"),
            "error"
          );
        }
        commit("changeCredentialData", {
          credential: res.credential,
          channelId,
        });
        resolve();
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      }
    });
  },

  editCredentialsData(
    { commit, state },
    { params, channelId, url, resolve, token, reject }
  ) {
    patchRequest({
      commit,
      token,
      url,
      params,
      callBack: (res) => {
        if (res.credential.active) {
          showNotification(
            commit,
            $i18n.t("editCredentialsDataActive"),
            "success"
          );
        } else {
          showNotification(
            commit,
            $i18n.t("editCredentialsDataInactive"),
            "error"
          );
        }
        commit("changeCredentialData", {
          credential: res.credential,
          channelId,
        });
        resolve();
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      }
    });
  },

  updateCompaniesChannel: patchAndCommit({
    url: ({ id }) => `/ticketing/company_channels/${id}`,
    notifyKey: "updateCompaniesChannel",
    mutation: "changeCompaniesChannel",
    payload: (res, { companyId, id }) => ({ active: res.active, companyId, id }),
  }),

  updatePartnersChannel: patchAndCommit({
    url: ({ id }) => `/ticketing/agency_channels/${id}`,
    params: ({ active }) => ({ on_by_admin: active }),
    notifyKey: "updatePartnersChannel",
    mutation: "changePartnersChannel",
    payload: (res, { id }) => ({ active: res.active, id }),
  }),

  updateNegoCodeChannel: patchAndCommit({
    url: ({ id }) => `/ticketing/agency_channels/${id}`,
    params: ({ negoCode }) => ({ nego_code: negoCode }),
    notifyKey: "updateChannelNegoCode",
    mutation: "changeChannelNegoCode",
    payload: (res, { id }) => ({ negoCode: res.nego_code, id }),
  }),

  updateResidentFaresCodeChannel: patchAndCommit({
    url: ({ id }) => `/ticketing/agency_channels/${id}`,
    params: ({ residentFaresCode }) => ({
      resident_fares_code: residentFaresCode,
    }),
    notifyKey: "updateChannelResidentFaresCode",
    mutation: "changeChannelResidentFaresCode",
    payload: (res, { id }) => ({
      residentFaresCode: res.resident_fares_code,
      id,
    }),
  }),

  updatePartnersAPI: patchAndCommit({
    url: ({ id }) => `/agencies/${id}/production_api_on`,
    notifyText: "API статус успешно обновлен",
    mutation: "changePartnersAPI",
    payload: (res, { id }) => ({
      production_api_on: res.production_api_on,
      id,
    }),
  }),

  updatePartnersCorporateNumberOn: patchAndCommit({
    url: ({ id }) => `/agencies/${id}/corporate_number_on`,
    notifyText: "Статус корпоративного номеру успішно оновлено",
    mutation: "changePartnersCorporateNumberOn",
    payload: (res, { id }) => ({
      corporate_number_on: res.corporate_number_on,
      id,
    }),
  }),

  updatePartnersGhostSegmentsRemarks: patchAndCommit({
    url: ({ id }) => `/agencies/${id}/ghost_segments_remarks_on`,
    notifyText: "Статус пасивних сегментів і ремарок успішно оновлено",
    mutation: "changePartnersGhostSegmentsRemarks",
    payload: (res, { id }) => ({
      ghost_segments_remarks_on: res.ghost_segments_remarks_on,
      id,
    }),
  }),

  getSearchTickets({ commit, state }, { data, ticketsCount, resolve }) {
    getRequest({
      commit,
      params: {
        headers: {
          Accept: "application/json, application/javascript, text/plain, */*",
        },
      },
      url: `/sales/?ticket%5Btext%5D=${JSON.stringify(
        data.text
      )}&ticket%5Bdate_from%5D=${JSON.stringify(
          data.date_from
      )}&ticket%5Bdate_to%5D=${JSON.stringify(
          data.date_to
      )}&page=1&per_page=${ticketsCount}`,
      callBack: (res) => {
        const parsed = JSON.parse(res);
        commit("setTickets", { tickets: parsed.tickets });
        resolve();
      },
    });
  },

  getTickets({ commit, state }, { data, ticketsCount, resolve }) {
    getRequest({
      commit,
      params: {
        headers: {
          Accept: "application/json, application/javascript, text/plain, */*",
        },
      },
      url: `/sales/?ticket%5Bdate_from%5D=${JSON.stringify(
        data.date_from
      )}&ticket%5Bdate_to%5D=${JSON.stringify(
        data.date_to
      )}&ticket%5Btext%5D=${JSON.stringify(
          data.text
      )}&page=1&per_page=${ticketsCount}`,
      callBack: (res) => {
        const parsed = JSON.parse(res);
        commit("setTickets", { tickets: parsed.tickets });
        resolve();
      },
    });
  },

  getAnalytics({ commit, state }, { data, resolve }) {
    getRequest({
      commit,
      params: {
        headers: {
          Accept: "application/json, application/javascript, text/plain, */*",
        },
      },
      url: `/analytics/?analytics%5Bdate_from%5D=${
        data.date_from
      }&analytics%5Bdate_to%5D=${data.date_to}`,
      callBack: (res) => {
        const parsed = JSON.parse(res);
        commit("setAnalytics", { analytics: parsed.analytics });
        resolve();
      },
    });
  },

  exportTickets({ commit, state }, { url, resolve }) {
    getRequest({
      commit,
      params: {
        headers: {
          Accept: "application/json, application/javascript, text/plain, */*",
        },
        responseType: "blob",
      },
      url,
      callBack: (res) => {
        const blob = new Blob([res]);
        let link = document.createElement("a");
        link.download = "Sales report.xlsx";
        link.href = URL.createObjectURL(blob);
        link.click();
        new AmplitudeEvent("Report");
        resolve();
      },
      errorCallBack: (response) => {
        if (response.status == 400) {
          showErrors($i18n.t("exportNoTickets"), commit);
        } else if (response.status == 413) {
          showNotification(commit, $i18n.t("reportSent"), "success");
        }
        resolve();
      },
    });
  },

  signIn({ commit, state }, { token, account }) {
    postRequest({
      commit,
      token,
      params: { account },
      url: "start_sign_in",
      callBack: (res) => {
        if (res.redirect_url) {
          window.location.href = res.redirect_url;
          return;
        }
        if (res.message) console.log("OTP:", res.message);
        commit("setLoginUI", { currentUI: res.account.auth_method });
        commit("setLoginEmail", { currentEmail: account.email });
      },
    });
  },

  sendOTP({ commit, state }, { token, account }) {
    postRequest({
      commit,
      token,
      params: { account },
      url: "sign_in",
      callBack: (res) => {
        window.location.href = "/";
      },
    });
  },

  sendVoiceOTP({ commit, state }, { token, account }) {
    postRequest({
      commit,
      token,
      params: { account },
      url: "send_voice_otp",
      callBack: (res) => {},
    });
  },

  sendAccountInfo({ commit, state }, { token, account }) {
    putRequest({
      commit,
      token,
      params: { account },
      url: "/onboarding",
      callBack: (res) => {
        commit("setOnboardingUI", { currentUI: "privacy" });
      },
    });
  },

  updateAccountEmail({ commit, state }, { token, account, resolve }) {
    postRequest({
      commit,
      token,
      params: { account },
      url: "/account/complete",
      callBack: (res) => {
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  sendPassword({ commit, state }, { token, account }) {
    postRequest({
      commit,
      token,
      params: { account },
      url: "sign_in",
      callBack: (res) => {
        window.location.href = "/";
      },
    });
  },

  changePhoneNumber({ commit, state }, { token, account, resolve }) {
    postRequest({
      commit,
      token,
      params: { account },
      url: "/account/phone",
      callBack: (res) => {
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  changeEmail({ commit, state }, { token, account, resolve }) {
    putRequest({
      commit,
      token,
      params: { account },
      url: "/account/email",
      callBack: (body, res) => {
        commit("setAccountEmail", { email: account.email });
        resolve(res);
      },
      errorCallBack: (res) => {
        resolve(res);
      },
    });
  },

  getAccountInfo({ commit, state }, { token }) {
    getRequest({
      commit,
      token,
      url: "/account/details",
      callBack: (res) => {
        commit("setAccountInfo", { accountInfo: res.data });
        if (res.data.language) {
          $i18n.locale = res.data.language;
          if (lsAvailable()) {
            localStorage.setItem("drct-lang", res.data.language);
          }
        }
        amplitude.setUserId(res.data.drct_id);
        if(res.data.user_type == 'Agent' || res.data.user_type =='Supervisor') {
          new AmplitudeUserProp('partner', res.data.agencies.map(a => a.name).join(','), 'set');
          new AmplitudeUserProp('partner_id', res.data.agencies.map(a => a.drct_id).join(','), 'set');
          new AmplitudeUserProp('localization', res.data.language, 'set');
        }
      },
    });
  },

  changeLanguage({ commit, state }, { token, language }) {
    putRequest({
      commit,
      token,
      params: { language },
      url: "/account/details/update_locale",
      callBack: (res) => {
        if (res.language) {
          $i18n.locale = res.language;
          if (lsAvailable()) {
            localStorage.setItem("drct-lang", res.language);
          }
        }
      },
    });
  },

  saveAccountInfo({ commit, state }, { token, account, resolve }) {
    putRequest({
      commit,
      token,
      params: { account },
      url: "/account/details",
      callBack: (res) => {
        showNotification(commit, $i18n.t("saveAccountInfo"), "success");
        commit("setAccountInfo", { accountInfo: res });
        resolve();
      },
    });
  },

  acceptPhoneChanging({ commit, state }, { token, account, resolve }) {
    putRequest({
      commit,
      token,
      params: { account },
      url: "/account/phone",
      callBack: (res) => {
        showNotification(commit, $i18n.t("acceptPhoneChanging"), "success");
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  acceptPrivacy({ commit, state }, { token }) {
    putRequest({
      commit,
      token,
      url: "/account/terms",
      callBack: (res) => {
        window.location.href = "/";
      },
    });
  },

  downloadPdfTicket({ commit, state }, { id, pass, number, token, resolve, custom_prices }) {
    getRequest({
      commit,
      token,
      params: {
        headers: { Accept: "application/pdf" },
        responseType: "arraybuffer",
      },
      url: `/sales/${id}?${custom_prices}`,
      callBack: (res) => {
        let blob = new Blob([res], { type: "application/pdf" });
        let link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `${pass}, ${number}`;
        link.click();
        resolve();
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  // TODO: Remove after PDF generation is implemented
  downloadTicket({ commit, state }, { id, token, resolve, custom_prices }) {
    getRequest({
      commit,
      token,
      url: `/sales/${id}?${custom_prices}`,
      callBack: (res) => {
        resolve(res);
      },
      errorCallBack: () => {
        resolve();
      },
    });
  },

  logOut({ commit, state }, { token }) {
    deleteRequest({
      commit,
      token,
      url: "/sign_out",
      callBack: (res) => {
        window.location.replace([window.location.origin, ""].join("/"));
      },
    });
  },

  logOutBack({ commit, state }, { token }) {
    deleteRequest({
      commit,
      token,
      url: "/sign_out",
      callBack: (res) => {
        window.location.replace([window.location.origin, ""].join("/sign_in"));
      },
    });
  },

  searchFlightsStream(
    { commit, state },
    {
      token,
      params,
      resolve,
      preserveExtraFilters = false,
      publishMode = "progressive",
      criteriaSource: sourceOverride,
      analyticsSource,
      spot
    }
  ) {
    const isAtomic = publishMode === "atomic";
    const connectionFilterRevision = state.connectionFilterRevision || 0;
    const source = sourceOverride || criteriaSource(params);
    const preservedRefreshContext = isAtomic
      ? ["command", "terminal_input", "filter_command", "prompts", "search_params"].reduce((context, key) => {
          if (Object.prototype.hasOwnProperty.call(state.searchResult || {}, key)) {
            context[key] = state.searchResult[key];
          }
          return context;
        }, {})
      : {};

    commit("setLastSearchParams", { params });
    if (!isAtomic) {
      commit("setSearchCommand", {
        command: params?.command ?? null,
        source: params?.source ?? null
      });
    }

    // Abort any previous stream before starting a new one
    if (currentStreamController) {
      currentStreamController.abort();
    }

    const analytics = new SearchAnalytics({
      params, source: analyticsSource, spot,
      emit: (name, properties) => new AmplitudeEvent(name, properties)
    });

    commit("setSearchState", { currentState: "loading" });
    if (!isAtomic) commit("clearSearchResult");

    const streamController = new AbortController();
    currentStreamController = streamController;
    const signal = streamController.signal;

    const initialSearchParams = Array.isArray(params?.fare_params?.flights)
      ? params.fare_params
      : null;
    let searchResults = {
      origin_destinations: [],
      search_id: analytics.context.search_id,
      search_source: analytics.context.source,
      ...(initialSearchParams ? { search_params: initialSearchParams } : {})
    };

    if (initialSearchParams && !isAtomic) {
      commit("setSearchResult", { result: { ...searchResults } });
    }
    let totalChunks = 0;
    let receivedChunks = 0;
    let successfulChunks = 0;
    let failedChunks = 0;
    let noFlightsChunks = 0;
    let noFlightsMessage = null;
    let sawDone = false;
    let streamIntegrityError = false;
    let settled = false;
    let currentEvent = null;
    let searchEvent = null;
    let searchError = null;
    // Carriers this search was actually narrowed to, as the server reports it
    // in the meta event: empty for a full search, a list for a scoped one. It
    // stays null until that event arrives, because until then we have not been
    // told anything. Reaches the store only once the offers are on screen.
    let carrierScope = null;

    const settle = (status) => {
      if (settled) return;
      settled = true;
      analytics.finish(status, {
        event: searchEvent, errorMessage: searchError
      });
      resolve?.({ status });
    };

    const rejectAtomicRefresh = () => {
      if (currentStreamController === streamController) currentStreamController = null;
      commit("setSearchState", { currentState: "result" });
      commit("finishStreaming");
      showNotification(commit, $i18n.t("refreshFailed"), "error");
      settle("error");
    };

    const returnToSearch = () => {
      commit("clearSearchResult");
      commit("setSearchState", { currentState: "start" });
      commit("setServerCarrierScope", { carriers: null });
      commit("finishStreaming");
    };

    const finalizeStream = () => {
      if (settled || currentStreamController !== streamController) return;

      const incompleteAtomicStream = isAtomic && (
        !sawDone ||
        streamIntegrityError ||
        !searchResults.origin_destinations.length ||
        receivedChunks !== totalChunks ||
        (failedChunks > 0 && successfulChunks === 0)
      );

      if (incompleteAtomicStream) {
        rejectAtomicRefresh();
        return;
      }

      const hasResults = searchResults.origin_destinations.length > 0;
      const incomplete = !sawDone || streamIntegrityError || receivedChunks !== totalChunks;
      if (incomplete) searchError ||= 'Search stream interrupted';

      const allChannelsReportedNoFlights =
        totalChunks > 0 &&
        receivedChunks === totalChunks &&
        noFlightsChunks === totalChunks;

      currentStreamController = null;

      if (!isAtomic && !hasResults && allChannelsReportedNoFlights) {
        returnToSearch();
        showNotification(commit, noFlightsMessage, "error");
        settle("error");
        return;
      }

      if (!isAtomic && !hasResults) {
        commit("setSearchState", { currentState: "error" });
        commit("setSearchResult", { result: {} });
        commit("setServerCarrierScope", { carriers: null });
        commit("finishStreaming");
        showNotification(commit, $i18n.t("searchFailed"), "error");
        settle("error");
        return;
      }

      const finalResult = isAtomic
        ? { ...searchResults, ...preservedRefreshContext }
        : searchResults;
      commit(isAtomic ? "replaceSearchResult" : "setSearchResult", { result: finalResult });
      commit("setSearchState", { currentState: "result" });
      // Written only now: the scope describes the offers on screen, and
      // until the stream ends there are none to describe.
      commit("setServerCarrierScope", { carriers: carrierScope });
      commit("finishStreaming");
      settle(incomplete || failedChunks > noFlightsChunks ? "partial" : "success");
    };
  
    fetch('/offers', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({ ...params, search_context: analytics.context }),
      signal
    }).then(response => {
      analytics.receive({ trace_id: response.headers.get('x-trace-id') });
      if (!response.ok) {
        return response.json().catch(() => ({})).then(body => {
          searchEvent = body.search_event;
          const requestError = new Error(`HTTP error! status: ${response.status}`);
          const error = body.errors?.[0];

          // Form validation already returns the localized Copilot error in the
          // normal JSON API shape. Preserve it instead of replacing it with a
          // generic streaming failure.
          requestError.userMessage = error?.detail || error?.title;
          throw requestError;
        });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function readStream() {
        return reader.read().then(({ done, value }) => {
          if (currentStreamController !== streamController) {
            settle("aborted");
            return;
          }

          if (done) {
            console.log('Stream completed');
            finalizeStream();
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (line.trim() === '') continue;

            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim(); // 'meta', 'chunk', 'done'
              continue;
            }

            if (line.startsWith('data: ')) {
              const raw = line.slice(6);

              try {
                const data = JSON.parse(raw);
                const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
                console.log(`Received ${currentEvent || 'unknown'}:`, data, 'Timestamp:', timestamp);

                // META: channels, passengers, request_type, request_source, sort, offers_params, prompts, filters
                if (currentEvent === 'meta') {
                  analytics.receive(data.search_analytics);
                  searchResults = {
                    ...searchResults,
                    ...data
                  };

                  carrierScope = data.carrier_scoped ? (data.filters?.carriers || []) : [];

                  if (!isAtomic) {
                    commit("setSearchResult", { result: { ...searchResults } });
                  }

                  if (!isAtomic && data.filters && typeof data.filters === 'object') {
                    // The stop buttons are usable before Copilot's metadata arrives.
                    // A later server intent must respect even a selection cleared by the user.
                    const manualConnections = (state.connectionFilterRevision || 0) !== connectionFilterRevision
                      ? {
                          direct: state.searchFilters.direct === true,
                          up_to_one_connection: state.searchFilters.up_to_one_connection === true
                        }
                      : null;
                    const filters = responseCriteria(data);
                    if (manualConnections) {
                      ['direct', 'up_to_one_connection', 'connections_from', 'connections_to']
                        .forEach(key => delete filters[key]);
                    }
                    commit("applyIntent", {
                      filters,
                      source: sourceOverride || criteriaSource(params, data.request_source),
                      preserveExtraFilters
                    });
                    if (manualConnections) {
                      commit("applyIntent", {
                        filters: manualConnections,
                        source: 'ui',
                        preserveExtraFilters: true
                      });
                    }
                    if (data.sort?.sort) {
                      commit("setSortPreference", {
                        value: data.sort.sort,
                        source: sourceOverride || criteriaSource(params, data.request_source)
                      });
                    }
                  }
                }

                // CHUNK: chunk of origin_destinations
                else if (currentEvent === 'chunk') {
                  receivedChunks += 1;
                  if (data.error) {
                    failedChunks += 1;
                    searchError ||= data.error;
                    if (data.error_code === 'flights_not_found') {
                      noFlightsChunks += 1;
                      noFlightsMessage ||= data.error;
                    }
                  } else {
                    successfulChunks += 1;
                  }

                  const result = data.result;

                  if (Array.isArray(result)) {
                    const destinationsWithIds = result.map((destination, index) => ({
                      ...destination,
                      id: searchResults.origin_destinations.length + index
                    }));

                    searchResults.origin_destinations = [
                      ...searchResults.origin_destinations,
                      ...destinationsWithIds
                    ];

                    // console.log('set s (chunk array)', searchResults);
                    if (!isAtomic) {
                      commit("setSearchResult", { result: { ...searchResults } });
                    }

                  } else if (Array.isArray(result?.origin_destinations)) {
                    const destinationsWithIds = result.origin_destinations.map((destination, index) => ({
                      ...destination,
                      id: searchResults.origin_destinations.length + index
                    }));

                    searchResults.origin_destinations = [
                      ...searchResults.origin_destinations,
                      ...destinationsWithIds
                    ];

                    // console.log('set s (chunk object)', searchResults);
                    if (!isAtomic) {
                      commit("setSearchResult", { result: { ...searchResults } });
                    }
                  }
                }

                // DONE: total
                else if (currentEvent === 'done') {
                  sawDone = true;
                  searchEvent = data.search_event;
                  if (typeof data.total === 'number') {
                    totalChunks = data.total;
                    console.log('Stream done. Total chunks:', totalChunks);
                  }
                }

                // fallback
                else if (!currentEvent && data.result) {
                  const result = data.result;
                }

              } catch (error) {
                streamIntegrityError = true;
                console.error('Error parsing chunk data:', error, 'raw:', raw);
              }
            }
          }

          if (sawDone) {
            finalizeStream();
            reader.cancel().catch(() => undefined);
            return;
          }

          return readStream();
        });
      }

      return readStream();
    }).catch(error => {
      // Check if the error is due to intentional abort (new search started)
      if (error.name === 'AbortError' || currentStreamController !== streamController) {
        console.log('Stream aborted (new search started)');
        settle("aborted");
        return;
      }

      console.error('Stream failed:', error);
      searchError = error.userMessage || error.message;
      currentStreamController = null;

      if (isAtomic) {
        rejectAtomicRefresh();
        return;
      }

      if (error.userMessage) {
        returnToSearch();
        showNotification(commit, error.userMessage, "error");
        settle("error");
        return;
      }

      if (searchResults.origin_destinations && searchResults.origin_destinations.length) {
        commit("setSearchState", { currentState: "result" });
        // Partial offers were still fetched with the scope the meta event named,
        // so it holds. Their being incomplete is a different problem.
        commit("setServerCarrierScope", { carriers: carrierScope });
      } else {
        commit("setSearchState", { currentState: "error" });
        commit("setSearchResult", { result: {} });
        // Nothing is on screen, so nothing is known about what it covers.
        commit("setServerCarrierScope", { carriers: null });
        showNotification(commit, $i18n.t("searchFailed"), "error");
      }

      commit("finishStreaming");
      settle(searchResults.origin_destinations?.length ? "partial" : "error");
    });
  },

  requestFilters({ commit, dispatch, state, getters }, { token, params, resolve }) {
    postRequest({
      commit,
      token,
      params: {
        ...params,
        current_filters: state.searchFilters || {},
        current_sort: state.sortPreference?.value || 'smart',
        trip_type: state.searchResult?.request_type || 'OW',
        current_route: state.searchResult?.search_params?.flights?.map((flight) => ({
          departure_airport_code: flight.departure_airport_code,
          arrival_airport_code: flight.arrival_airport_code
        }))
      },
      url: "/filters",
      callBack: (res) => {
        const recognized = responseCriteria(res);
        if (res.recognized !== true || !res.filters || !validFilterState(recognized)) {
          resolve?.({ status: 'unrecognized' });
          return;
        }

        const nextCarriers = recognized.carriers || [];
        // Taken before the optimistic update so a failed search can undo it.
        const snapshot = captureFilterState(state);

        // The recognized state reaches the UI now, without waiting for offers:
        // buttons, bubbles, counter, sorting and the frontend filters all read
        // the store.
        commit("applyIntent", { filters: recognized, source: "ai_filter", preserveSources: true });
        if (res.sort) commit("setSortPreference", { value: res.sort, source: "ai_filter" });
        commit("setSearchState", { currentState: "result" });

        // The snapshot holds the carriers from before the optimistic update, so
        // "did the carriers actually move" is still answerable here.
        if (!getters.carrierSearchNeeded(nextCarriers, snapshot.searchFilters.carriers)) {
          resolve?.({ status: 'success' });
          return;
        }

        dispatch("refreshCarrierScope", {
          carriers: nextCarriers,
          nextFilters: recognized,
          token,
          source: "ai_filter",
          snapshot,
          resolve
        });
      },
      errorCallBack: () => {
        resolve?.({ status: 'error' });
      },
    });
  },

  refreshCarrierScope(
    { commit, dispatch, state },
    { carriers, nextFilters, token, source = 'ui', snapshot, resolve }
  ) {
    const searchParams = state.searchResult?.search_params;
    if (!searchParams) {
      resolve?.({ status: 'error' });
      return;
    }

    const fareParams = { ...searchParams };
    const preferences = { ...(fareParams.preferences || {}) };
    delete fareParams.carriers;
    delete preferences.permitted_carriers;

    if (carriers.length > 0) {
      fareParams.carriers = carriers;
      preferences.permitted_carriers = carriers;
    }
    if (Object.keys(preferences).length > 0) fareParams.preferences = preferences;
    else delete fareParams.preferences;

    const candidateFilters = nextFilters || (() => {
      const candidate = { ...(state.searchFilters || {}) };
      if (carriers.length > 0) candidate.carriers = carriers;
      else delete candidate.carriers;
      return candidate;
    })();

    // Captured before the optimistic update, whether the caller took the
    // snapshot itself or the update happens right here.
    const rollbackTo = snapshot || captureFilterState(state);

    // Applied before the search, not in its resolve: the filters must show up
    // without waiting for offers, on the UI path as much as the AI one.
    commit("applyIntent", {
      filters: candidateFilters,
      source,
      preserveSources: true
    });
    const connectionFilterRevision = state.connectionFilterRevision || 0;

    dispatch("searchFlightsStream", {
      token,
      params: { fare_params: fareParams },
      resolve: (result) => {
        // The scope itself is written by the search that owns it, once its
        // offers are on screen. 'aborted' is skipped on purpose: a newer
        // search already owns the state.
        if (result?.status === 'error') {
          // Airlines can be reset while the stop buttons remain usable. Roll
          // back the failed carrier change without erasing a newer stop choice.
          const manualConnections = (state.connectionFilterRevision || 0) !== connectionFilterRevision
            ? {
                direct: state.searchFilters.direct === true,
                up_to_one_connection: state.searchFilters.up_to_one_connection === true,
                connections_from: null,
                connections_to: null,
                ...(state.searchFilters.country_transfer == null ? { country_transfer: null } : {}),
                ...(state.searchFilters.overnight_transfer == null ? { overnight_transfer: null } : {})
              }
            : null;
          commit("restoreFilterState", { snapshot: rollbackTo });
          if (manualConnections) {
            commit("applyIntent", {
              filters: manualConnections,
              source: 'ui',
              preserveExtraFilters: true
            });
          }
        }
        resolve?.(result);
      },
      preserveExtraFilters: true,
      publishMode: "atomic",
      criteriaSource: source,
      analyticsSource: source === 'ai_filter' ? 'Magic' : 'Form',
      spot: source === 'ai_filter' ? 'ai_filter' : 'refresh'
    });
  },

  deleteFilter({ commit, state }, { token, searchId, filterId, resolve }) {
    deleteRequest({
      commit,
      token,
      url: `/search_results/${searchId}?filter_key=${filterId}`,
      callBack: (res) => {
        commit("setSearchResult", { result: res });
        commit("setSearchState", { currentState: "result" });
        resolve();
      },
      errorCallBack: () => {
        commit("setSearchState", { currentState: "start" });
        commit("setSearchResult", { result: {} });
        resolve();
      },
    });
  },

  searchPassengerProfiles({ commit }, { token, lastName, resolve, reject }) {
    getRequest({
      commit,
      token,
      silent: true,
      url: `/passenger_profiles?last_name=${encodeURIComponent(lastName)}`,
      callBack: (body) => {
        if (resolve) resolve(body);
      },
      errorCallBack: (error) => {
        if (reject) reject(error);
      },
    });
  },

  savePassengerProfile({ commit }, { token, profile, resolve, reject }) {
    postRequest({
      commit,
      token,
      silent: true,
      params: profile,
      url: "/passenger_profiles",
      callBack: (body) => {
        if (resolve) resolve(body);
      },
      errorCallBack: (error) => {
        if (reject) reject(error);
      },
    });
  },

  bookFlight({ commit, state }, { token, params, resolve, reject }) {
    postRequest({
      commit,
      token,
      params,
      url: "/bookings",
      callBack: (res, initialResponse) => {
        params.traceId = getTraceId(initialResponse)
        createIntercomEvent('bookFlight', params)
        if (res.fake_booking || res.provider_status == "in_progress") {
          window.location.replace([window.location.origin, `bookings?locator=${res.locator}`].join("/"));
        } else {
          commit("pushNotification", {
            notification: buildBookingSuccessNotification(res, params.instant_payment)
          });
        }

        if (res.error) {
          showNotification(commit, res.error, "error");
        }
        resolve(res);
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      },
    });
  },

  bookVirtualInterline({ commit, state }, { token, params, resolve, reject }) {
    postRequest({
      commit,
      token,
      params,
      url: "/bookings/virtual_interline",
      callBack: (res, initialResponse) => {
        params.traceId = getTraceId(initialResponse)
        createIntercomEvent('bookVirtualInterline', params, state.accountInfo.company)

        const bookings = [res.outbound_booking, res.inbound_booking]
        bookings.forEach((booking) => {
          commit("pushNotification", {
            notification: buildBookingSuccessNotification(booking, false)
          });
          if (booking.error) {
            showNotification(commit, booking.error, "error");
          }
        });

        resolve(res);
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      },
    });
  },

  getFaresDetails(
    { commit, state },
    { token, url, fareId, destinationId, resolve, reject, silent = false }
  ) {
    getRequest({
      commit,
      token,
      url,
      silent,
      callBack: (res, initialResponse) => {
        createIntercomEvent('getFaresDetails', {
          fareId, destinationId, traceId: getTraceId(initialResponse)
        })

        if (res.price) {
          commit("addPriceDetails", {
            details: res,
            fareIds: fareId,
            destinationId,
          });
        }

        if (res.fare_segments.length != 0) {
          commit("addFaresDetails", {
            details: res,
            fareIds: fareId,
            destinationId,
          });
        }

        if (res.flights) {
          commit("addFlightsDetails", {
            details: res,
            fareIds: fareId,
            destinationId,
          });
        }

        if (res.ancillaries) {
          commit("addAncillariesDetails", {
            details: res,
            fareIds: fareId,
            destinationId,
          });
        }

        resolve();
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      },
    });
  },

  getMoreFares({ commit, state }, { id, searchResultId, destinationId, command, source, resolve, reject }) {
    const query = new URLSearchParams({ search_result_id: searchResultId });
    if (command) query.append("command", command);
    if (source) query.append("source", source);
    customRequest({
      commit,
      method: "get",
      url: `offers/${id}/details?${query.toString()}`,
      callBack: (res) => {
        commit("setFares", { destinationId, fares: res });
        if (resolve) {
          resolve();
        }
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      },
    });
  },

  getViDetails({ commit }, { token, params, destinationId, resolve, reject }) {
    postRequest({
      commit,
      token,
      params,
      url: "/offers_search/virtual_interlines/details",
      callBack: (res) => {
        commit("setViDetailsFares", { destinationId, viDetails: res });
        if (resolve) resolve();
      },
      errorCallBack: (error) => {
        if (reject) reject(error);
      },
      silent: true,
    });
  },

  createPrivateJetRequest({ commit }, { token, params, resolve, reject }) {
    postRequest({
      commit,
      token,
      params,
      url: "/private_jet_requests",
      callBack: (res) => {
        if (resolve) {
          resolve(res);
        }
      },
      errorCallBack: (error) => {
        if (reject) {
          reject(error);
        }
      },
    });
  },

  issueBooking(
    { commit, state },
    { token, locator, resolve, reject, reshop, fakeBooking }
  ) {
    commit("setBookingLoader", { currentState: true });
    postRequest({
      commit,
      token,
      params: { locator, reshop },
      url: "/bookings/issue",
      callBack: (body, res) => {
        createIntercomEvent('issueBooking', {
          locator,
          reshop,
          fakeBooking,
          traceId: getTraceId(res)
        })
        if (res.status == 201) {
          commit("setOpenedBooking", { booking: body });
          commit("changeBookingDetails", {
            locator: body.locator,
            cancel_before: body.cancel_before,
            status: body.status,
          });
          if (fakeBooking) {
            if (res.tickets_present) {
              showNotification(commit, $i18n.t('issueBooking'), "success")
            } else {
              showNotification(commit, $i18n.t('issueFakeBooking'), "success")
            }
          }
          else {
            showNotification(commit, $i18n.t("issueBooking"), "success");
          }
        }

        commit("setBookingLoader", { currentState: false });
        resolve(body);
      },
      errorCallBack: (error) => {
        commit("setBookingLoader", { currentState: false });
        if (reject) {
          reject(error);
        }
      },
    });
  },

  issueAncillary({ commit, state }, { token, params, locator, resolve }) {
    commit("setBookingLoader", { currentState: true });

    postRequest({
      commit,
      token,
      params,
      url: `bookings/${locator}/ancillaries`,
      callBack: (body, res) => {
        if (res.status == 201) {
          commit("setOpenedBooking", { booking: body });
          commit("changeBookingPrice", {
            locator: body.locator,
            price: body.price,
          });
          const notificationMessage = body.manual_ancillary
            ? $i18n.t("processAncillaries")
            : body.async_emd_issue
              ? $i18n.t("asyncBookAncillaries")
              : $i18n.t("bookAncillaries");
          showNotification(commit, notificationMessage, "success");
        }

        commit("setBookingLoader", { currentState: false });
        resolve();
      },
      errorCallBack: () => {
        commit("setBookingLoader", { currentState: false });
        resolve();
      },
    });
  },

  deleteAncillary({ commit, state }, { token, locator, ancillaryId, resolve }) {
    commit("setBookingLoader", { currentState: true });

    deleteRequest({
      commit,
      token,
      url: `bookings/${locator}/ancillaries/${ancillaryId}`,
      callBack: (body, res) => {
        if (res.status == 200) {
          commit("setOpenedBooking", { booking: body });
          commit("changeBookingPrice", {
            locator: body.locator,
            price: body.price,
          });
          showNotification(commit, $i18n.t("deleteAncillary") || "Ancillary deleted", "success");
        }

        commit("setBookingLoader", { currentState: false });
        if (resolve) resolve();
      },
      errorCallBack: () => {
        commit("setBookingLoader", { currentState: false });
        if (resolve) resolve();
      },
    });
  },

  openAncillary({ commit, state }, { token, locator, type, resolve }) {
    commit("setBookingLoader", { currentState: true });

    getRequest({
      commit,
      token,
      url: `bookings/${locator}/ancillaries?type=${type}`,
      callBack: (body, res) => {
        commit("setBookingLoader", { currentState: false });
        resolve(body);
      },
      errorCallBack: () => {
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  openSearchAncillary({ commit, state }, { token, fare_id, type, resolve }) {
    getRequest({
      commit,
      token,
      url: `offers/${fare_id}/ancillaries?type=${type}`,
      callBack: (body, res) => {
        resolve(body);
      },
    });
  },

  repriceBooking({ commit, dispatch, state }, { token, locator }) {
    commit("setBookingLoader", { currentState: true });
    postRequest({
      commit,
      token,
      url: `/bookings/commit_price`,
      params: { locator },
      callBack: (res) => {
        commit("setOpenedBooking", { booking: res });
        if (res.locator) window.history.pushState({}, "", `/bookings?locator=${res.locator}`);
        showNotification(commit, "Booking was confirmed", "success");
        commit("setBookingLoader", { currentState: false });
        dispatch("openBooking", { token, locator: res.locator || locator });
      },
      errorCallBack: () => {
        commit("setPriceCalculationError", { currentState: true });
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  reshopBooking({ commit, state }, { token, locator }) {
    commit("setBookingLoader", { currentState: true });
    postRequest({
      commit,
      token,
      url: `/bookings/reshop`,
      params: { locator },
      callBack: (res) => {
        commit("setOpenedBooking", { booking: res });
        if (res.locator) window.history.pushState({}, "", `/bookings?locator=${res.locator}`);
        showNotification(commit, "Booking was confirmed", "success");
        commit("setBookingLoader", { currentState: false });
      },
      errorCallBack: () => {
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  voidBooking({ commit, state, dispatch }, { token, locator }) {
    commit("setBookingLoader", { currentState: true });
    deleteRequest({
      commit,
      token,
      url: `/bookings/${locator}`,
      callBack: (res) => {
        const otherBookingOpened =
          state.openedBooking?.locator && state.openedBooking.locator !== locator;

        if (res.manual_void) {
          if (!otherBookingOpened) {
            dispatch("openBooking", { token, locator });
          }
          showNotification(commit, $i18n.t("manualVoidBooking"), "success");
        } else {
          showNotification(commit, $i18n.t("voidBooking"), "success");
          commit("removeOpenedBooking", { locator });
          if (!otherBookingOpened) {
            window.history.pushState({}, "", "/bookings");
          }
        }
        if (state.hiddenBooking?.locator === locator) {
          commit("setHiddenBooking", { booking: {} });
        }

        commit("setBookingLoader", { currentState: false });
      },
      errorCallBack: () => {
        if (state.hiddenBooking?.locator === locator) {
          commit("setHiddenBooking", { booking: {} });
        }
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  createRefundRequest({ commit, state }, { token, locator, resolve }) {
    commit("setBookingLoader", { currentState: true });
    postRequest({
      commit,
      token,
      url: `/orders/${locator}/refund_requests/create`,
      callBack: (body, res) => {
        commit("setBookingLoader", { currentState: false });
        resolve(body);
      },
      errorCallBack: () => {
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  confirmRefundRequest({ commit, state }, { token, locator }) {
    commit("setBookingLoader", { currentState: true });
    postRequest({
      commit,
      token,
      url: `/orders/${locator}/refund_requests/confirm`,
      callBack: () => {
        commit("pushNotification", {
          notification: {
            type: "success",
            text: $i18n.t("refundBooking"),
            start: false,
            finish: false,
            permanent: true
          }
        });
        commit("removeOpenedBooking", { locator });
        window.history.pushState({}, "", "/bookings");
        commit("setBookingLoader", { currentState: false });
      },
      errorCallBack: () => {
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  cancelBooking({ commit, state }, { token, locator }) {
    commit("setBookingLoader", { currentState: true });
    deleteRequest({
      commit,
      token,
      url: `/bookings/${locator}`,
      callBack: (res) => {
        const otherBookingOpened =
          state.openedBooking?.locator && state.openedBooking.locator !== locator;

        showNotification(commit, $i18n.t("cancelBooking"), "success");
        commit("removeOpenedBooking", { locator });
        if (!otherBookingOpened) {
          window.history.pushState({}, "", "/bookings");
        }
        commit("setBookingLoader", { currentState: false });
        if (state.hiddenBooking?.locator === locator) {
          commit("setHiddenBooking", { booking: {} });
        }
      },
      errorCallBack: () => {
        if (state.hiddenBooking?.locator === locator) {
          commit("setHiddenBooking", { booking: {} });
        }
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  openBooking({ commit, state }, { token, locator, resolve }) {
    commit("setBookingLoader", { currentState: true });
    getRequest({
      commit,
      token,
      url: `/bookings/${locator}`,
      callBack: (res, initialResponse) => {
        createIntercomEvent('openBooking', {
          locator,
          traceId: getTraceId(initialResponse)
        })

        commit("setOpenedBooking", { booking: res });
        if (res.locator) window.history.pushState({}, "", `/bookings?locator=${res.locator}`);
        commit("setBookingLoader", { currentState: false });
        commit("changeBookingDetails", {
          locator: res.locator,
          cancel_before: res.cancel_before,
        });
        commit("selectBookingList", { selected: res.status });
        if (resolve) {
          resolve();
        }
      },
      errorCallBack: (err) => {
        if (err.status == 416) {
          commit("removeBooking", { locator });
          window.history.pushState({}, "", "/bookings");
        } else if (err.status == 400) {
          window.history.pushState({}, "", "/bookings");
        } else {
          commit("setOpenedBooking", { booking: { locator, openError: true } });
          if (resolve) {
            resolve();
          }
        }
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  updateBooking({ commit, state }, { token, params, resolve }) {
    commit("setBookingLoader", { currentState: true });
    postRequest({
      commit,
      token,
      params,
      url: "/bookings/modify",
      callBack: (res) => {
        showNotification(commit, $i18n.t("bookingUpdated"), "success");
        commit("setBookingLoader", { currentState: false });
        commit("setOpenedBooking", { booking: res });
        if (resolve) {
          resolve(res);
        }
      },
      errorCallBack: () => {
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  calculateNewPrice({ commit, state }, { token, params, resolve }) {
    commit("setBookingLoader", { currentState: true });
    postRequest({
      commit,
      token,
      params,
      url: "bookings/quote_price",
      callBack: (res) => {
        commit("setBookingLoader", { currentState: false });
        resolve(res);
      },
      errorCallBack: () => {
        commit("setPriceCalculationError", { currentState: true });
        commit("setBookingLoader", { currentState: false });
      },
    });
  },

  changeCompanyVisibility({ commit, state }, { id, shared_bookings, token }) {
    patchRequest({
      commit,
      token,
      url: `/ticketing/company_shared_bookings/${id}`,
      params: { active: shared_bookings },
      callBack: (res) => {
        showNotification(commit, $i18n.t("changeCompanyVisibility"), "success");
        commit("changeCompanyVisibility", { shared_bookings: res.active, id });
      },
    });
  },

  generateApiKey({ commit, state }, { token }) {
    putRequest({
      commit,
      token,
      params: {},
      url: "/account/api_token",
      callBack: (res) => {
        commit("changeApiKey", { token: res.data.token });
        showNotification(commit, $i18n.t("apiKeyGenerated"), "success");
      },
    });
  },

  updateApiKey({ commit, state }, { token }) {
    putRequest({
      commit,
      token,
      params: {},
      url: "/account/api_token",
      callBack: (res) => {
        commit("changeApiKey", { token: res.data.token });
        showNotification(commit, $i18n.t("apiKeyUpdated"), "success");
      },
    });
  },

  aiAsk({ commit, state }, { token, params, resolve }) {
    postRequest({
      commit,
      token,
      params,
      url: "/admin_panel/ask",
      callBack: (res) => {
        console.log(params, res);
        commit("setAIAskResult", { result: res });
        resolve();
      },
      errorCallBack: (res) => {
        commit("setAIAskResult", { result: res.body });
        resolve();
      },
    });
  },

  aiExec({ commit, state }, { token, params, resolve }) {
    postRequest({
      commit,
      token,
      params,
      url: "/admin_panel/execute",
      callBack: (res) => {
        console.log(params, res);
        commit("setAIAskResult", { result: res });
        resolve();
      },
      errorCallBack: (res) => {
        commit("setAIAskResult", { result: res.body });
        resolve();
      },
    });
  },

  createFlightsChangeRequest({ commit, state }, { token, locator, params, resolve }) {
    postRequest({
      commit,
      token,
      params,
      url: `/orders/${locator}/flights_change_requests/create`,
      callBack: (res) => {
        commit("setSearchReissue", { currentState: true });
        commit("setBookingLoader", { currentState: false });
        commit("setReissueSearchParams", { params: params })
        commit("setReissueOffers", { offers: res });
        commit("setSearchState", { currentState: "result" });
        resolve();
      },
      errorCallBack: (res) => {
        commit("setBookingLoader", { currentState: false });
        commit("setReissueOffers", { offers: [] });
        commit("setSearchState", { currentState: "result" });
        console.log(res);
        resolve();
      },
    });
  },

  reshopOffersPrice({ commit, state }, { token, locator, params, resolve }) {
    postRequest({
      commit,
      token,
      params,
      url: `/orders/${locator}/reshop_offers/price`,
      callBack: (res, fullRes) => {
        commit("setSearchReissue", { currentState: true });
        commit("setBookingLoader", { currentState: false });
        commit("setReissueOffer", { destinationId: params.destination_id, fareIds: params.fare_id, offer: res });
        resolve({ status: fullRes.status });
      },
      errorCallBack: (res) => {
        commit("setBookingLoader", { currentState: false });
        console.log(res);
        resolve({ status: res.status });
      }
    });
  },

  confirmFlightsChangeRequest({ commit, state }, { token, locator, params }) {
    postRequest({
      commit,
      token,
      params,
      url: `/orders/${locator}/flights_change_requests/confirm`,
      callBack: (res) => {
        window.location.replace([window.location.origin, ""].join(`/bookings?locator=${locator}`));
        showNotification(commit, $i18n.t("exchagedTitle"), "success");
      },
      errorCallBack: (res) => {
        commit("setBookingLoader", { currentState: false });
        console.log(res)
      },
    });
  },

  updateComment({ commit, state }, { token, params, resolve }) {
    commit("setBookingLoader", {currentState: true});
    postRequest({
      commit,
      token,
      params,
      url: "/bookings/comment",
      callBack: (res) => {
        showNotification(commit, $i18n.t("commentUpdated"), "success");
        commit("setBookingLoader", {currentState: false});
        if (resolve) {
          resolve(res);
        }
      },
      errorCallBack: (res) => {
        showNotification(commit, res.error, "error");
        commit("setBookingLoader", {currentState: false});
      },
    });
  }
};

function getCookie(name) {
  const matches = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") +
        "=([^;]*)"
    )
  );
  return matches ? matches[1] : undefined;
}

function setCookie(name, value, options) {
  options = options || {};

  let expires = options.expires;

  if (typeof expires == "number" && expires) {
    let d = new Date();
    d.setTime(d.getTime() + expires * 1000);
    expires = options.expires = d;
  }
  if (expires && expires.toUTCString) {
    options.expires = expires.toUTCString();
  }

  value = encodeURIComponent(value);
  let updatedCookie = name + "=" + value;

  for (var propName in options) {
    updatedCookie += "; " + propName;
    const propValue = options[propName];
    if (propValue !== true) {
      updatedCookie += "=" + propValue;
    }
  }

  document.cookie = updatedCookie;
}

function showErrors(errors, commit) {
  // Normalize errors to always be an array
  let errorArray;
  
  if (Array.isArray(errors)) {
    // Already an array
    errorArray = errors;
  } else if (typeof errors === 'string') {
    // String error message - wrap it in an error object
    errorArray = [{ text: errors, type: 'error' }];
  } else if (typeof errors === 'object' && errors !== null) {
    // Single error object - wrap it in an array
    errorArray = [errors];
  } else {
    // Unknown format, skip processing
    return;
  }
  
  for (let error of errorArray) {
    const notificationType = error.type === 'warning' ? 'warning' : 'error';
    if (error.detail) {
      showNotification(commit, error.detail, notificationType);
    } else if (error.title) {
      showNotification(commit, error.title, notificationType);
    } else if (error.text) {
      showNotification(commit, error.text, notificationType);
    }
  }
}

// Rejects with the `errors` strings of a failed request. They are toasted unless
// the caller renders them itself; a failure without them always toasts serverError.
function rejectWithErrors({ commit, error, reject, notify = true }) {
  const messages = errorMessages(error);
  if (!messages.length) {
    showNotification(commit, $i18n.t("serverError"), "error");
  } else if (notify) {
    messages.forEach((message) => showNotification(commit, message, "error"));
  }
  if (reject) reject(messages);
}

// Shows the errors as notifications and hands the HTTP status to the caller (409: duplicate).
function rejectWithStatus({ commit, error, reject }) {
  rejectWithErrors({ commit, error });
  if (reject) reject({ status: error?.status, messages: errorMessages(error) });
}

function errorMessages(error) {
  const errors = error?.body?.errors ?? error?.body?.error;
  const list = Array.isArray(errors) ? errors : [errors];
  return list
    .map((item) => (typeof item === "string" ? item : item?.detail || item?.title || item?.text))
    .filter(Boolean);
}

// Keeps the dashboard's agency channel in step with a link/unlink response.
function commitAgencyChannelCredential(commit, channelId, res) {
  if (res?.channel_credential) {
    commit("changeCredentialData", { credential: res.channel_credential, channelId });
  }
  if (Array.isArray(res?.carriers)) {
    commit("changeAgencyChannel", { channelId, attributes: { carriers: res.carriers } });
  }
}

function showNotification(commit, text, type) {
  commit("pushNotification", {
    notification: { text, type, start: false, finish: false },
  });
}

function buildBookingSuccessNotification(res, instantPayment) {
  let textMsg;
  if (instantPayment) {
    textMsg = `${$i18n.t('issue_1')} ${res.locator} ${$i18n.t('issue_2')}.`;
  } else if (res.price_expires_at || res.continuous_pricing_without_expiry) {
    textMsg = $i18n.t('booked_expires_at');
  } else {
    textMsg = `${$i18n.t('booked_1')} ${res.locator} ${$i18n.t('booked_2')} ${res.time_limit}.`;
  }

  // The price can move between the offer stage and the booking stage. When it
  // does, warn the agent (yellow) and spell out the change, e.g.
  // "Price changed from 100 UAH to 150 UAH".
  const priceChange = res.price_changed;
  if (priceChange) {
    const { from, to } = priceChange;
    textMsg += ` ${$i18n.t('price_changed_1')} ${from.amount} ${from.currency} ${$i18n.t('price_changed_2')} ${to.amount} ${to.currency}.`;
  }

  return {
    type: priceChange ? 'warning' : 'success',
    text: textMsg,
    start: false,
    finish: false,
    permanent: true,
    link: `/bookings?locator=${res.locator}`,
    link_text: $i18n.t('openBookingLink'),
  };
}

window.pushBackNotification = function(type, value) {
  if (type == "error") {
    showNotification(store.commit, value, "error");
  } else {
    showNotification(store.commit, value, "success");
  }
};

function getRequest(params = {}) {
  params = Object.assign({}, params, { method: "get" });
  return customRequest(params);
}

function postRequest(params = {}) {
  params = Object.assign({}, params, { method: "post" });
  return customRequest(params);
}

function deleteRequest(params = {}) {
  return overrideMethod("delete", params);
}

function putRequest(params = {}) {
  return overrideMethod("put", params);
}

function patchRequest(params = {}) {
  return overrideMethod("patch", params);
}

function patchAndCommit({
  url,
  params,
  notifyKey,
  notifyText,
  mutation,
  payload,
}) {
  return ({ commit }, { token, ...rest }) => {
    patchRequest({
      commit,
      token,
      params: params ? params(rest) : undefined,
      url: typeof url === "function" ? url(rest) : url,
      callBack: (res) => {
        showNotification(
          commit,
          notifyText || $i18n.t(notifyKey),
          "success"
        );
        commit(mutation, payload(res, rest));
      },
    });
  };
}

// NOTE: for CSRF support
function overrideMethod(method, params) {
  let headers = { "X-HTTP-METHOD-OVERRIDE": method };
  if (params.headers) {
    params.headers = Object.assign({}, params.headers, headers);
  } else {
    params.headers = headers;
  }
  params.method = "post";
  return customRequest(params);
}

async function customRequest({
  method,
  url,
  token,
  params,
  commit,
  callBack,
  errorCallBack,
  headers = {},
  silent = false,
}) {
  try {
    headers = Object.assign({}, { "X-CSRF-Token": token }, headers);
    let res = await http[method](url, params, { headers: headers });
    
    if (res.body.errors) {
      if (errorCallBack) {
        errorCallBack(res);
      }
      if (!silent) {
        showErrors(res.body.errors, commit);
      }
    } else {
      callBack(res.body, res);
    }
  } catch (ex) {
    console.log(ex)
    if (errorCallBack) {
      errorCallBack(ex);
    }
    if (!silent) {
      if (ex.body && ex.body.errors) {
        showErrors(ex.body.errors, commit);
      } else {
        showNotification(commit, $i18n.t("serverError"), "error");
      }
    }
  }
}

function lsAvailable() {
  const test = "test";
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

function getTraceId(initialResponse) {
  if (
    initialResponse &&
    initialResponse.headers &&
    initialResponse.headers.map &&
    initialResponse.headers.map['x-trace-id'] &&
    initialResponse.headers.map['x-trace-id'][0]
  ) {
    return initialResponse.headers.map['x-trace-id'][0]
  }

  return null
}
